"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import Image, { type StaticImageData } from "next/image";
import LocaleLink from "next/link";
import type * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/pop-ui/pop-toast";
import { QuickChatWrapper } from "@/components/pop-ui/quick-chat-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { LanguageSwitcherInFooter } from "@/lib/i18n/language-switcher";
import { getCurrentAreaInClient, getCurrentLanguageInClient } from "@/lib/utils.client";
import { useORPC } from "@/providers/orpc-provider";
import { trackSubscription } from "@/track/gtm";
import { CMSLink, CMSText } from "../cms/components/cms-items";
import { getTranslatedText } from "../cms/utils.server";
import { communityInfo, contactInfo, copyrightInfo, creditCardList, mediaList } from "./footer.config";
import { MobileFooter } from "./mobile-footer";

// import GooglePlay from '@/assets/images/common/pc-googlepay.png'
// import AppStore from '@/assets/images/common/pc-applestore.png'
// import GooglePlayQR from '@/assets/images/common/googleplay-qr.png'
// import AppStoreQR from '@/assets/images/common/appstore-qr.png'
// import PopmartLogo from '@/assets/images/common/POPMART.png'

// 社交媒体图标组件
function SocialIcon(t0) {
  const $ = _c(10);
  const {
    href,
    src
  } = t0;
  const isExternalUrl = href.startsWith("http://") || href.startsWith("https://");
  if (isExternalUrl) {
    let t1;
    if ($[0] !== src) {
      t1 = <Image unoptimized={true} src={src} alt="social media" width={30} height={30} />;
      $[0] = src;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== href || $[3] !== t1) {
      t2 = <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center text-black transition-colors hover:text-gray-600">{t1}</a>;
      $[2] = href;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  const t1 = href as any;
  let t2;
  if ($[5] !== src) {
    t2 = <Image unoptimized={true} src={src} alt="social media" width={30} height={30} />;
    $[5] = src;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <LocaleLink href={t1} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center text-black transition-colors hover:text-gray-600">{t2}</LocaleLink>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

// 社交媒体栏组件
function SocialBar(t0) {
  const $ = _c(26);
  const {
    socialMedia
  } = t0;
  useState(null);
  const {
    t
  } = useTranslation();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== socialMedia?.platforms || $[1] !== t) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const enabledPlatforms = socialMedia?.platforms?.filter(_temp) || [];
      if (enabledPlatforms.length === 0) {
        t7 = null;
        break bb0;
      }
      const filteredMediaList = mediaList.filter(item => enabledPlatforms.some(platform_0 => platform_0.platform.toUpperCase() === item.platform.toUpperCase()));
      if (filteredMediaList.length === 0) {
        t7 = null;
        break bb0;
      }
      t6 = "border-c border-t";
      t5 = "container";
      t3 = "flex items-center justify-between gap-6 py-6";
      let t8;
      if ($[9] !== t) {
        t8 = t("footer.followUs");
        $[9] = t;
        $[10] = t8;
      } else {
        t8 = $[10];
      }
      if ($[11] !== t8) {
        t4 = <div className="flex items-center gap-6"><span className="text-px24 dinpro-700 font-medium">{t8}</span></div>;
        $[11] = t8;
        $[12] = t4;
      } else {
        t4 = $[12];
      }
      t1 = "ml-12 flex gap-3";
      t2 = filteredMediaList.map(item_0 => {
        const platformData = enabledPlatforms.find(platform_1 => platform_1.platform.toUpperCase() === item_0.platform.toUpperCase());
        return <SocialIcon key={item_0.platform} href={platformData?.url || "#"} src={item_0.src} />;
      });
    }
    $[0] = socialMedia?.platforms;
    $[1] = t;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[13] !== t1 || $[14] !== t2) {
    t8 = <div className={t1}>{t2}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t8) {
    t9 = <div className={t3}>{t4}{t8}</div>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t5 || $[21] !== t9) {
    t10 = <div className={t5}>{t9}</div>;
    $[20] = t5;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t6) {
    t11 = <div className={t6}>{t10}</div>;
    $[23] = t10;
    $[24] = t6;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
// 版权信息
function _temp(platform) {
  return platform.enabled;
}
function CopyRight(t0) {
  const $ = _c(16);
  const {
    paymentData
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== paymentData) {
    const getPaymentMethods = () => {
      if (!paymentData || Object.keys(paymentData).length === 0) {
        return creditCardList;
      }
      if (paymentData.payments && Array.isArray(paymentData.payments) && paymentData.payments.length === 0) {
        return [];
      }
      if (paymentData.payments && Array.isArray(paymentData.payments) && paymentData.payments.length > 0) {
        return creditCardList.filter(card => paymentData.payments.some(p => p.toUpperCase() === card.method.toUpperCase()));
      }
      return [];
    };
    const paymentMethodsToShow = getPaymentMethods();
    t4 = "border-t bg-black py-6";
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        borderColor: "var(--color-footer-gray-alpha)"
      };
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    t1 = "container relative mx-auto flex max-w-[1440px] items-center justify-between";
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="text-footer-gray whitespace-nowrap text-xs">©POP MART</div>;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t3 = paymentMethodsToShow.length > 0 && <div className="flex gap-2">{paymentMethodsToShow.map(_temp2)}</div>;
    $[0] = paymentData;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t6 = <div className={t1}>{t2}{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <div className={t4} style={t5}>{t6}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}

// 辅助组件：页脚链接
function _temp2(card_0) {
  return <div key={card_0.method} className="flex items-center justify-center rounded"><Image src={card_0.src} alt="paymentData" width={32} height={20} style={{
      width: "32px",
      height: "20px"
    }} unoptimized={true} /></div>;
}
function FooterLink(t0) {
  const $ = _c(7);
  const {
    children,
    className,
    link
  } = t0;
  if (!link) {
    const t1 = `text-footer-gray mt-4 text-sm leading-[18px] ${className || ""}`;
    let t2;
    if ($[0] !== children || $[1] !== t1) {
      t2 = <span className={t1}>{children}</span>;
      $[0] = children;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const t1 = `text-footer-gray mt-4 block overflow-hidden text-ellipsis whitespace-nowrap text-sm leading-[18px] transition-colors hover:text-white ${className || ""}`;
  let t2;
  if ($[3] !== children || $[4] !== link || $[5] !== t1) {
    t2 = <CMSLink link={link} className={t1}>{children}</CMSLink>;
    $[3] = children;
    $[4] = link;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

// 页脚部分组件
function FooterSection(t0) {
  const $ = _c(15);
  const {
    title,
    buttons: t1
  } = t0;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t1 || $[1] !== title) {
    const buttons = t1 === undefined ? [] : t1;
    const lang = getCurrentLanguageInClient();
    t4 = "w-[170px] text-sm";
    if ($[6] !== title) {
      t5 = <span className="dinpro-bold text-sm text-white">{title}</span>;
      $[6] = title;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    t2 = "flex flex-col";
    t3 = buttons.map((item, index) => {
      const buttonData = item.button || item;
      const buttonKey = buttonData.id || `footer-link-${index}-${buttonData.link?.url || ""}`;
      return <FooterLink key={buttonKey} link={buttonData.link} className="mb-2"><CMSText text={getTranslatedText(lang, buttonData.text || {})} /></FooterLink>;
    });
    $[0] = t1;
    $[1] = title;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t3) {
    t6 = <div className={t2}>{t3}</div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}

// 客户端 Footer 组件，负责大屏和小屏渲染
export function Footer() {
  const $ = _c(57);
  const area = getCurrentAreaInClient();
  const lang = getCurrentLanguageInClient();
  const orpc = useORPC();
  const {
    t
  } = useTranslation();
  const t0 = orpc.cms.content.public_findLiveWithPagination.queryOptions({
    input: {
      group: "footer-batch-query",
      area: area?.toUpperCase(),
      includeChildren: true,
      platform: "web"
    }
  });
  let t1;
  if ($[0] !== t0) {
    t1 = {
      ...t0,
      staleTime: 0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: footerBatchResponse
  } = useQuery(t1);
  const footerBatch = (footerBatchResponse as any)?.items || [];
  const socialMedia = footerBatch.find(_temp3)?.children?.[0]?.data || {};
  const paymentData = footerBatch.find(_temp4)?.children?.[0]?.data || {};
  const customer = footerBatch.find(_temp5)?.children?.[0]?.data || {};
  const footerSections = footerBatch.filter(_temp6)?.[0]?.children || [];
  const [email, setEmail] = useState("");
  const getEmailFromCustomer = () => {
    if (!customer || Object.keys(customer).length === 0 || !customer.email) {
      return "support@popmart.com";
    }
    return customer.email;
  };
  const getServiceTime = () => {
    if (!customer || Object.keys(customer).length === 0 || !customer.serviceTime_trans) {
      return "Monday-Sunday 9:00am-18:00pm CST";
    }
    const translatedTime = getTranslatedText(lang, customer.serviceTime_trans || {});
    return translatedTime;
  };
  const isMobile = useIsMobile();
  if (isMobile) {
    return <MobileFooter footerSections={footerSections} lang={lang} area={area} socialMedia={socialMedia} paymentData={paymentData} customer={customer} />;
  }
  let t2;
  if ($[2] !== email || $[3] !== t) {
    t2 = () => {
      if (!email.includes("@")) {
        toast.error(t("footer.subscribe.invalidEmail"));
        trackSubscription({
          status: "failed"
        });
        return;
      }
      (window as any)?.quickChatApi?.setContactProperties({
        email
      });
      toast.success(t("footer.subscribe.success"));
      trackSubscription({
        status: "success"
      });
      setEmail("");
    };
    $[2] = email;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleSubscribe = t2;
  let t3;
  if ($[5] !== t) {
    t3 = t(communityInfo.title);
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="dinpro-bold text-sm text-white">{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t(communityInfo.description);
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <p className="my-3 text-sm">{t5}</p>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t(communityInfo.placeholder);
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e => setEmail(e.target.value);
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== email || $[17] !== t7) {
    t9 = <Input id="footer-email-subscription" name="email" type="email" autoComplete="email" placeholder={t7} className="h-[38px] border-none bg-transparent" value={email} onChange={t8} />;
    $[16] = email;
    $[17] = t7;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t(communityInfo.signUp);
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== handleSubscribe || $[22] !== t10) {
    t11 = <Button variant="link" className="mr-2 bg-transparent p-0" onClick={handleSubscribe}>{t10}</Button>;
    $[21] = handleSubscribe;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t9) {
    t12 = <div className="rounded-xs flex items-center bg-white">{t9}{t11}</div>;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t) {
    t13 = t(contactInfo.title);
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13) {
    t14 = <div className="dinpro-bold text-sm text-white">{t13}</div>;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t) {
    t15 = t(contactInfo.chatTitle);
    $[31] = t;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t15) {
    t16 = <span className="text-white">{t15}</span>;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t) {
    t17 = t(contactInfo.chatButton);
    $[35] = t;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t17) {
    t18 = <QuickChatWrapper><Button variant="link" size="sm" className="text-footer-gray cursor-pointer bg-transparent p-0 hover:text-white">{t17}</Button></QuickChatWrapper>;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t) {
    t19 = t(contactInfo.emailTitle);
    $[39] = t;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t19) {
    t20 = <span className="text-white">{t19}</span>;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const t21 = `mailto:${getEmailFromCustomer()}`;
  let t22;
  if ($[43] !== t) {
    t22 = t(contactInfo.emailButton);
    $[43] = t;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t21 || $[46] !== t22) {
    t23 = <Button variant="link" size="sm" className="text-footer-gray bg-transparent p-0 hover:text-white" asChild={true}><a href={t21}>{t22}</a></Button>;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t) {
    t24 = t(contactInfo.helpCenter);
    $[48] = t;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t24) {
    t25 = <Button variant="link" size="sm" className="text-footer-gray text-decoration-thickness-[1px] text-underline-offset-[2px] mt-[12px] inline-flex bg-transparent p-0 hover:text-white" asChild={true}><LocaleLink href="/help/faqs">{t24}</LocaleLink></Button>;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <LanguageSwitcherInFooter />;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  const t27 = "w-946/1440 flex justify-end gap-6";
  const t28 = footerSections.length > 0 ? footerSections.map(section => {
    const sectionTitle = getTranslatedText(lang, section.title_trans || {}) || getTranslatedText("en", section.title_trans || {}) || t("footer.noTitle");
    const buttons = section.data?.buttonGroup?.buttons || [];
    return <FooterSection key={section.id} title={sectionTitle} buttons={buttons} />;
  }) : <div>{t("footer.loadingLinks")}</div>;
  let t29;
  if ($[53] !== t28) {
    t29 = <div className={t27}>{t28}</div>;
    $[53] = t28;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== paymentData) {
    t30 = <CopyRight paymentData={paymentData} />;
    $[55] = paymentData;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  return <><SocialBar socialMedia={socialMedia} /><footer className="text-footer-gray w-full bg-black text-sm"><div className="container pb-6 pt-[60px]"><div className="flex justify-between gap-8"><div className="w-450/1440 flex flex-col">{t4}{t6}{t12}<div className="mt-6"><div className="flex flex-col items-start">{t14}<div className="border-footer-gray mt-[14px] flex w-full items-center justify-between border px-3 py-2 leading-none"><div className="dinpro-bold flex flex-col gap-2">{t16}<span className="mr-1">{getServiceTime()}</span></div>{t18}</div><div className="border-footer-gray mt-[14px] flex w-full items-center justify-between border px-3 py-2 leading-none"><div className="dinpro-bold flex flex-col gap-2">{t20}<span className="text-footer-gray text-sm">{getEmailFromCustomer()}</span></div>{t23}</div>{t25}{t26}</div></div></div>{t29}</div></div>{t30}</footer></>;
}
function _temp6(item_2) {
  return item_2.alias === "footer-config";
}
function _temp5(item_1) {
  return item_1.alias === "customer-service-config";
}
function _temp4(item_0) {
  return item_0.alias === "payment-config";
}
function _temp3(item) {
  return item.alias === "social-media-config";
}